import { useState, useEffect } from 'react';
import { globalCSSSystem, Theme } from '../utils/globalCSSSystem';
import styles from './ThemeToggle.module.css';

/**
 * Theme Toggle Component
 * Provides UI for switching between themes and testing CSS system
 */
const ThemeToggle = ({ showDiagnostics = false, className = '' }) => {
  const [currentTheme, setCurrentTheme] = useState('auto');
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [showDiagPanel, setShowDiagPanel] = useState<boolean>(false);

  useEffect(() => {
    // Get initial theme
    const themeManager = globalCSSSystem.getThemeManager();
    setCurrentTheme(themeManager.getTheme());

    // Listen for theme changes
const handleThemeChange = (event: Event) => {
  const customEvent = event as CustomEvent<{ theme: Theme }>;
  setCurrentTheme(customEvent.detail.theme);
};

    document.addEventListener('themechange', handleThemeChange);

    // Run initial diagnostics if requested
    if (showDiagnostics) {
      setDiagnostics(globalCSSSystem.runDiagnostics());
}

    return () => {
      document.removeEventListener('themechange', handleThemeChange);
};
  }, [showDiagnostics]);

  const handleThemeToggle = (theme: Theme) => {
    const themeManager = globalCSSSystem.getThemeManager();
    themeManager.setTheme(theme);
  };

  const runDiagnostics = () => {
    const report = globalCSSSystem.runDiagnostics();
    setDiagnostics(report);
    setShowDiagPanel(true);
  };

const getThemeIcon = (theme: Theme) => {
  switch (theme) {
    case 'light':
      return '☀️';
    case 'dark':
      return '🌙';
    case 'high-contrast':
      return '🎯';
    case 'auto':
    default:
      return '🌓';
  }
};

  const themes = [
    { value: 'auto', label: 'Auto' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'high-contrast', label: 'High Contrast' },
  ];

  return (
    <div className={`${(styles as any).themeToggleContainer} ${className}`}>
      <div className={(styles as any).themeToggle}>
        <label htmlFor='theme-select' className={(styles as any).themeToggleLabel}>
          Theme:
        </label>
        <div className={(styles as any).themeSelectWrapper}>
          <select
            id='theme-select'
            value={currentTheme}
            onChange={(e) => handleThemeToggle(e.target.value as Theme)}
            className={`${(styles as any).themeSelect} focus-ring`}
            aria-label='Select theme'
          >
            {themes.map((theme: any) => (
              <option key={theme.value} value={theme.value}>
                {getThemeIcon(theme.value)} {theme.label}
              </option>
            ))}
          </select>
        </div>

        {showDiagnostics && (
          <button
            onClick={runDiagnostics}
            className={`${(styles as any).diagnosticsButton} focus-ring`}
            type='button'
            aria-label='Run CSS diagnostics'
          >
            🔍 Diagnostics
          </button>
        )}
      </div>

      {showDiagPanel && diagnostics && (
        <div className={(styles as any).diagnosticsPanel}>
          <div className={(styles as any).diagnosticsHeader}>
            <h3>CSS System Diagnostics</h3>
            <button
              onClick={() => setShowDiagPanel(false)}
              className={`${(styles as any).closeButton} focus-ring`}
              type='button'
              aria-label='Close diagnostics panel'
            >
              ✕
            </button>
          </div>

          <div className={(styles as any).diagnosticsContent}>
            <div className={(styles as any).diagnosticSection}>
              <h4>Browser Support</h4>
              <div
                className={`${(styles as any).statusItem} ${diagnostics.cssVariableSupport ? (styles as any).success : (styles as any).error}`}
              >
                <span className={(styles as any).statusIcon}>
                  {diagnostics.cssVariableSupport ? '✅' : '❌'}
                </span>
                <span>
                  CSS Custom Properties:{' '}
                  {diagnostics.cssVariableSupport
                    ? 'Supported'
                    : 'Not Supported'}
                </span>
              </div>
            </div>

            <div className={(styles as any).diagnosticSection}>
              <h4>CSS Variables</h4>
              <div
                className={`${(styles as any).statusItem} ${diagnostics.variableCheck.allAvailable ? (styles as any).success : (styles as any).warning}`}
              >
                <span className={(styles as any).statusIcon}>
                  {diagnostics.variableCheck.allAvailable ? '✅' : '⚠️'}
                </span>
                <span>
                  {diagnostics.variableCheck.missing.length === 0
                    ? 'All required variables available'
                    : `${diagnostics.variableCheck.missing.length} variables missing`}
                </span>
              </div>

              {diagnostics.variableCheck.missing.length > 0 && (
                <div className={(styles as any).missingVariables}>
                  <h5>Missing Variables:</h5>
                  <ul>
                    {diagnostics.variableCheck.missing.map((variable: any) => (
                      <li key={variable}>
                        <code>{variable}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className={(styles as any).diagnosticSection}>
              <h4>Performance</h4>
              <div
                className={`${(styles as any).statusItem} ${diagnostics.performance.slowLoadTime ? (styles as any).warning : (styles as any).success}`}
              >
                <span className={(styles as any).statusIcon}>
                  {diagnostics.performance.slowLoadTime ? '⚠️' : '✅'}
                </span>
                <span>
                  CSS Load Time: {diagnostics.performance.loadTime.toFixed(2)}ms
                  {diagnostics.performance.slowLoadTime ? ' (Slow)' : ' (Good)'}
                </span>
              </div>
            </div>

            {diagnostics.recommendations.length > 0 && (
              <div className={(styles as any).diagnosticSection}>
                <h4>Recommendations</h4>
                <ul className={(styles as any).recommendations}>
                  {diagnostics.recommendations.map((rec: any, index: number) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
